// =============================================
// COMPONENTE: IMPORT/EXPORT MODAL
// =============================================
// Arquivo: ImportExportModal.tsx
// Descrição: Modal para importar/exportar fluxos em JSON
// =============================================

import React, { useState, useRef, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import {
  Download, Upload, Copy, Check, X, FileJson,
  AlertCircle, Info, Clock, Trash2
} from 'lucide-react';
import ModalPortal from '../Common/ModalPortal';
import './ImportExportModal.css';

// =============================================
// INTERFACES
// =============================================

interface ImportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'export' | 'import';
  flowData?: {
    nodes: Node[];
    edges: Edge[];
    metadata?: {
      name?: string;
      description?: string;
      version?: string;
      author?: string;
      createdAt?: string;
    };
  };
  onImport?: (data: { nodes: Node[]; edges: Edge[] }) => void;
}

interface FlowExport {
  version: string;
  metadata: {
    name: string;
    description: string;
    author: string;
    createdAt: string;
    exportedAt: string;
  };
  nodes: Node[];
  edges: Edge[];
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const ImportExportModal: React.FC<ImportExportModalProps> = ({
  isOpen,
  onClose,
  mode,
  flowData,
  onImport
}) => {
  const [activeTab, setActiveTab] = useState<'export' | 'import' | 'history'>(mode);
  const [jsonContent, setJsonContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importData, setImportData] = useState<FlowExport | null>(null);
  const [importMode, setImportMode] = useState<'replace' | 'merge'>('replace');
  const [backupHistory, setBackupHistory] = useState<FlowExport[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // =============================================
  // HISTÓRICO DE BACKUPS (LOCALSTORAGE)
  // =============================================

  const loadBackupHistory = useCallback(() => {
    try {
      const history = localStorage.getItem('flow-backup-history');
      if (history) {
        const parsed = JSON.parse(history);
        setBackupHistory(parsed.slice(0, 10)); // Máximo 10 backups
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    }
  }, []);

  const saveToHistory = useCallback((exportData: FlowExport) => {
    try {
      const history = localStorage.getItem('flow-backup-history');
      const parsed = history ? JSON.parse(history) : [];
      const updated = [exportData, ...parsed].slice(0, 10); // Máximo 10
      localStorage.setItem('flow-backup-history', JSON.stringify(updated));
      setBackupHistory(updated);
    } catch (err) {
      console.error('Erro ao salvar no histórico:', err);
    }
  }, []);

  const deleteFromHistory = useCallback((index: number) => {
    try {
      const updated = backupHistory.filter((_, i) => i !== index);
      localStorage.setItem('flow-backup-history', JSON.stringify(updated));
      setBackupHistory(updated);
    } catch (err) {
      console.error('Erro ao deletar do histórico:', err);
    }
  }, [backupHistory]);

  // =============================================
  // EXPORT HANDLER
  // =============================================

  const handleExport = () => {
    if (!flowData) return;

    const exportData: FlowExport = {
      version: '1.0.0',
      metadata: {
        name: flowData.metadata?.name || 'Fluxo sem nome',
        description: flowData.metadata?.description || '',
        author: flowData.metadata?.author || 'Desconhecido',
        createdAt: flowData.metadata?.createdAt || new Date().toISOString(),
        exportedAt: new Date().toISOString()
      },
      nodes: flowData.nodes,
      edges: flowData.edges
    };

    const json = JSON.stringify(exportData, null, 2);
    setJsonContent(json);

    // Salvar no histórico automaticamente
    saveToHistory(exportData);
  };

  // =============================================
  // DOWNLOAD JSON FILE
  // =============================================

  const handleDownload = () => {
    if (!jsonContent) handleExport();

    const blob = new Blob([jsonContent || JSON.stringify(flowData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // =============================================
  // COPY TO CLIPBOARD
  // =============================================

  const handleCopy = async () => {
    if (!jsonContent) handleExport();

    try {
      await navigator.clipboard.writeText(jsonContent || JSON.stringify(flowData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Erro ao copiar para a área de transferência');
    }
  };

  // =============================================
  // IMPORT FROM FILE
  // =============================================

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      parseImportData(content);
    };
    reader.readAsText(file);
  };

  // =============================================
  // PARSE IMPORT DATA
  // =============================================

  const parseImportData = (content: string) => {
    try {
      setError(null);
      const data = JSON.parse(content) as FlowExport;

      // Validar estrutura
      if (!data.nodes || !Array.isArray(data.nodes)) {
        throw new Error('Estrutura inválida: campo "nodes" não encontrado ou inválido');
      }
      if (!data.edges || !Array.isArray(data.edges)) {
        throw new Error('Estrutura inválida: campo "edges" não encontrado ou inválido');
      }

      setImportData(data);
      setJsonContent(content);
    } catch (err: any) {
      setError(err.message || 'Erro ao processar arquivo JSON');
      setImportData(null);
    }
  };

  // =============================================
  // IMPORT FROM TEXTAREA
  // =============================================

  const handleTextareaImport = () => {
    parseImportData(jsonContent);
  };

  // =============================================
  // CONFIRM IMPORT
  // =============================================

  const handleConfirmImport = () => {
    if (!importData || !onImport) return;

    // Gerar novos IDs para os nós para evitar conflitos
    const idMap = new Map<string, string>();
    const newNodes = importData.nodes.map(node => {
      const newId = `${node.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      idMap.set(node.id, newId);
      return {
        ...node,
        id: newId,
        // Offset position no modo merge para evitar sobreposição
        position: importMode === 'merge' ? {
          x: node.position.x + 100,
          y: node.position.y + 100
        } : node.position
      };
    });

    const newEdges = importData.edges.map(edge => ({
      ...edge,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target
    }));

    // Se for modo merge, adicionar aos nós/edges existentes
    if (importMode === 'merge' && flowData) {
      onImport({
        nodes: [...flowData.nodes, ...newNodes],
        edges: [...flowData.edges, ...newEdges]
      });
    } else {
      // Modo replace - substituir tudo
      onImport({ nodes: newNodes, edges: newEdges });
    }

    onClose();
  };

  // =============================================
  // LIFECYCLE
  // =============================================

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(mode); // Reseta para o modo inicial
      loadBackupHistory(); // Carrega histórico
      if (mode === 'export') {
        handleExport();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, loadBackupHistory]);

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="import-export-modal-overlay" onClick={onClose}>
        <div className="import-export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="import-export-modal-header">
          <div className="import-export-modal-title">
            <FileJson size={20} />
            <h2>Backup & Restore</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs de navegação */}
        <div className="import-export-tabs">
          <button
            className={`tab ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            <Download size={16} />
            <span>Exportar</span>
          </button>
          <button
            className={`tab ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            <Upload size={16} />
            <span>Importar</span>
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <Clock size={16} />
            <span>Histórico ({backupHistory.length})</span>
          </button>
        </div>

        <div className="import-export-modal-content">
          {activeTab === 'export' ? (
            <>
              <div className="info-banner">
                <Info size={16} />
                <p>
                  O fluxo será exportado em formato JSON. Você pode salvar o arquivo
                  ou copiar o conteúdo para compartilhar.
                </p>
              </div>

              {flowData?.metadata && (
                <div className="flow-metadata">
                  <div className="metadata-item">
                    <span className="metadata-label">Nome:</span>
                    <span className="metadata-value">{flowData.metadata.name || 'Sem nome'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Nós:</span>
                    <span className="metadata-value">{flowData.nodes.length}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Conexões:</span>
                    <span className="metadata-value">{flowData.edges.length}</span>
                  </div>
                </div>
              )}

              <div className="json-viewer">
                <div className="json-viewer-header">
                  <FileJson size={16} />
                  <span>Conteúdo JSON</span>
                </div>
                <pre className="json-content">{jsonContent}</pre>
              </div>

              <div className="action-buttons">
                <button className="btn-secondary" onClick={handleCopy}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiado!' : 'Copiar JSON'}
                </button>
                <button className="btn-primary" onClick={handleDownload}>
                  <Download size={16} />
                  Download Arquivo
                </button>
              </div>
            </>
          ) : activeTab === 'import' ? (
            <>
              <div className="info-banner">
                <Info size={16} />
                <p>
                  Faça upload de um arquivo JSON ou cole o conteúdo abaixo para importar um fluxo.
                </p>
              </div>

              {/* Modo de importação */}
              <div className="import-mode-selector">
                <label>Modo de Importação:</label>
                <div className="radio-group">
                  <label className={`radio-option ${importMode === 'replace' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                    />
                    <div className="radio-content">
                      <span className="radio-title">Substituir</span>
                      <span className="radio-description">Substitui todo o fluxo atual</span>
                    </div>
                  </label>
                  <label className={`radio-option ${importMode === 'merge' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="importMode"
                      value="merge"
                      checked={importMode === 'merge'}
                      onChange={() => setImportMode('merge')}
                    />
                    <div className="radio-content">
                      <span className="radio-title">Adicionar</span>
                      <span className="radio-description">Adiciona ao fluxo existente</span>
                    </div>
                  </label>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              <button
                className="upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={20} />
                <span>Selecionar Arquivo JSON</span>
              </button>

              <div className="divider">
                <span>ou</span>
              </div>

              <div className="json-input-area">
                <label>Cole o JSON aqui:</label>
                <textarea
                  className="json-textarea"
                  value={jsonContent}
                  onChange={(e) => setJsonContent(e.target.value)}
                  placeholder='{"version":"1.0.0","nodes":[...],"edges":[...]}'
                  rows={10}
                />
                <button
                  className="btn-secondary btn-small"
                  onClick={handleTextareaImport}
                  disabled={!jsonContent}
                >
                  Validar JSON
                </button>
              </div>

              {error && (
                <div className="error-banner">
                  <AlertCircle size={16} />
                  <p>{error}</p>
                </div>
              )}

              {importData && (
                <div className="import-preview">
                  <div className="import-preview-header">
                    <Check size={16} />
                    <span>Fluxo válido!</span>
                  </div>
                  <div className="flow-metadata">
                    <div className="metadata-item">
                      <span className="metadata-label">Nome:</span>
                      <span className="metadata-value">{importData.metadata?.name || 'Sem nome'}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-label">Versão:</span>
                      <span className="metadata-value">{importData.version}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-label">Nós:</span>
                      <span className="metadata-value">{importData.nodes.length}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-label">Conexões:</span>
                      <span className="metadata-value">{importData.edges.length}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="action-buttons">
                <button className="btn-secondary" onClick={onClose}>
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  onClick={handleConfirmImport}
                  disabled={!importData}
                >
                  <Upload size={16} />
                  {importMode === 'merge' ? 'Adicionar ao Fluxo' : 'Importar Fluxo'}
                </button>
              </div>
            </>
          ) : (
            /* Tab de Histórico */
            <>
              <div className="info-banner">
                <Info size={16} />
                <p>
                  Histórico dos últimos 10 backups salvos localmente. Clique em um backup para restaurá-lo.
                </p>
              </div>

              {backupHistory.length === 0 ? (
                <div className="empty-state">
                  <Clock size={48} />
                  <h3>Nenhum backup encontrado</h3>
                  <p>Exporte um fluxo para criar seu primeiro backup.</p>
                </div>
              ) : (
                <div className="backup-history-list">
                  {backupHistory.map((backup, index) => (
                    <div key={index} className="backup-item">
                      <div className="backup-info">
                        <div className="backup-header">
                          <FileJson size={16} />
                          <span className="backup-name">{backup.metadata.name}</span>
                        </div>
                        <div className="backup-meta">
                          <span className="backup-date">
                            {new Date(backup.metadata.exportedAt).toLocaleString('pt-BR')}
                          </span>
                          <span className="backup-stats">
                            {backup.nodes.length} nós • {backup.edges.length} conexões
                          </span>
                        </div>
                        {backup.metadata.description && (
                          <p className="backup-description">{backup.metadata.description}</p>
                        )}
                      </div>
                      <div className="backup-actions">
                        <button
                          className="btn-icon btn-restore"
                          onClick={() => {
                            setImportData(backup);
                            setActiveTab('import');
                          }}
                          title="Restaurar este backup"
                        >
                          <Upload size={16} />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => deleteFromHistory(index)}
                          title="Deletar backup"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};

export default ImportExportModal;
